import React, { useMemo } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import {
  BodyText,
  Button,
  IllusEmpty_2,
  Image,
  TextLink,
  TextLinkifyTags,
  theme,
  useResponsive,
} from '@sg/garnish';

export const ForgotToScanError = (props: ForgotToScanErrorProps) => {
  const { error, onSupport, onClose, onBack } = props;

  const { match } = useResponsive();
  const { formatMessage } = useIntl();

  const links = useMemo(
    () => [
      {
        tag: 'contact_support',
        description: formatMessage(messages.contactSupport),
        onPress: onSupport,
      },
    ],
    [formatMessage, onSupport],
  );

  return (
    <View style={match([styles.containerXS, styles.containerSM])}>
      <View style={styles.content}>
        <Image source={IllusEmpty_2} style={styles.illustration} />

        <BodyText style={styles.title} sizeMatch={['24']}>
          <FormattedMessage {...messages.title} />
        </BodyText>

        <TextLinkifyTags style={styles.text} sizeMatch={['16']} links={links}>
          {error}
        </TextLinkifyTags>
      </View>

      <View style={styles.content}>
        <Button palette="kaleLightest" size="large-wide" onPress={onClose}>
          <FormattedMessage {...messages.close} />
        </Button>

        {onBack ? (
          <TextLink sizeMatch={['16']} style={styles.link} onPress={onBack}>
            <FormattedMessage {...messages.submitAnother} />
          </TextLink>
        ) : null}
      </View>
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerXS: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing['4'],
    paddingBottom: theme.spacing['10'],
  },
  containerSM: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing['10'],
    paddingBottom: theme.spacing['20'],
  },
  content: {
    alignItems: 'center',
    gap: theme.spacing['6'],
  },
  illustration: {
    width: 200,
    height: 200,
  },
  title: {
    color: theme.colors.KALE,
    textAlign: 'center',
  },
  text: {
    textAlign: 'center',
  },
  link: {
    color: theme.colors.SPINACH,
    textAlign: 'center',
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'We could not place this request.',
    description: 'Forgot To Scan | Error | Title',
  },
  contactSupport: {
    defaultMessage: 'contact support',
    description: 'Forgot To Scan | Error | Contact support',
  },
  submitAnother: {
    defaultMessage: 'Submit another request',
    description: 'Forgot To Scan | Error | Submit another request',
  },
  close: {
    defaultMessage: 'Close',
    description: 'Forgot To Scan | Error | Close',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ForgotToScanErrorProps = {
  error: string;
  onSupport: () => void;
  onClose: () => void;
  onBack?: () => void;
};
